import { defaultTo, ifElse } from 'aa_common/front-end/helpers'
import { useIntervalApiCall } from 'aa_common/front-end/hooks/useIntervalApiCall'
import { RadioChangeEvent } from 'antd/lib/radio'
import { CORPORATE_INCOME_TAX, StatusJob } from 'common/constants'
import { SupportedTextCharset } from 'common/models'
import { PresenterJob, ReportsService } from 'common/open-api'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { REPORT_TYPE } from '../../corporate-income-tax/components/filter'
import { EXPORT_TYPE } from '../components/corporate-income-tax-detail'
import { detailReportSlice } from '../store'

export const useCorporateIncomeTaxDetail = (termId: number, reportId: number, reportType: number) => {
  const [exportType, setExportType] = useState<EXPORT_TYPE>(EXPORT_TYPE.PDF)
  const [jobId, setJobId] = useState<string | undefined>(undefined)
  const [isShowModal, setIsShowModal] = useState(false)
  const isGetByAssetType = +reportType === REPORT_TYPE.BY_ASSET_TYPE
  const { assetCategoryIds, assetTypeCodes } = defaultTo(
    detailReportSlice.useSlice(state => state?.filters),
    {}
  )

  useEffect(() => {
    if (termId && reportId) {
      const pagination = { pageSize: 4 }
      if (+reportId === CORPORATE_INCOME_TAX.TABLE_16_TYPE_7) {
        pagination.pageSize = 12
      }
      detailReportSlice.setFilters(
        prevFilters => ({ ...prevFilters, termId, reportType, pagination, reportTemplateId: reportId }),
        true
      )
    }
  }, [termId, reportId, reportType])

  detailReportSlice.usePrepare()

  const sumIds = useMemo(
    () => ifElse(isGetByAssetType, assetTypeCodes?.join(','), assetCategoryIds?.join(',')),
    [isGetByAssetType, assetCategoryIds, assetTypeCodes]
  )

  const triggerExport = useCallback(
    async (charset?: string) => {
      setIsShowModal(false)
      const result = await ReportsService.corporateTaxExportList({
        term_id: termId,
        report_template_id: reportId,
        export_file_format: exportType,
        charset: charset ?? SupportedTextCharset[1],
        report_type: reportType,
        sum_ids: sumIds,
      })
      setJobId(result?.data?.id)
      setIsShowModal(false)
    },
    [termId, reportId, exportType, isGetByAssetType, sumIds, reportType]
  )

  const handleExport = useCallback(async () => {
    if (exportType === 'csv') {
      setIsShowModal(true)
    } else {
      await triggerExport()
    }
  }, [exportType, sumIds])

  const checkJob = () => {
    if (jobId) {
      return ReportsService.corporateTaxJobDetail(jobId)
    }
  }

  const { data } = useIntervalApiCall<PresenterJob>(
    () => checkJob(),
    3000,
    data => data?.status === StatusJob.JobStatusCompleted || data?.status === StatusJob.JobStatusTimeOut,
    data => {
      if (data?.status === StatusJob.JobStatusCompleted) {
        window.open(data?.data?.url ?? '')
      }
    },
    30000,
    [jobId]
  )

  const handleExportTypeChange = useCallback((e: RadioChangeEvent) => {
    setExportType(e.target.value)
  }, [])

  return {
    exportType,
    handleExport,
    triggerExport,
    handleExportTypeChange,
    isShowModal,
    setIsShowModal,
    exportStatus: data?.status,
  }
}
